import * as Phaser from "phaser";
import {
  Asteroid,
  Bullet,
  Resource,
  Ship,
  distance,
  splitSize,
  wrap,
} from "../entities";
import type { AsteroidSize } from "../entities";

export const GAME_WIDTH = 900;
export const GAME_HEIGHT = 720;

const BASE_ROTATE_SPEED = 2;
const BASE_THRUST_ACCEL = 200;
const REVERSE_ACCEL = 130;
const MAX_SPEED = 210;
const DRIVE_MULT_PER_LEVEL = 1.15;
const BULLET_SPEED = 300;
const BULLET_LIFETIME_MS = 900;
const BASE_FIRE_COOLDOWN_MS = 220;
const FIRE_COOLDOWN_MULT = 0.85;
const MAX_BULLETS = 5;
const SPAWN_INVULN_MS = 2200;
const BASE_ASTEROIDS = 3;
const RESOURCE_LIFETIME_MS = Infinity;
const RESOURCE_DRIFT_FACTOR = 0.2;
const SURPLUS_PER_PICKUP = 1;
const ROUND_DURATION_MS = 30000;
const BASE_PICKUP_RADIUS_BONUS = 0;
const PICKUP_RADIUS_PER_LEVEL = 6;
const TRACTOR_RANGE = 220;
const TRACTOR_PULL_ACCEL = 260;
const TRACTOR_MAX_PULL_SPEED = 210;
const HOMING_RANGE = 220;
const HOMING_CONE_COS = Math.cos((45 * Math.PI) / 180);
const HOMING_TURN_RATE = 2.75;
const SENSOR_CONE_COS = Math.cos((10 * Math.PI) / 180);
const SENSOR_RANGE = 500;
const INITIAL_HULL_INTEGRITY = 3;

export interface RoundEndPayload {
  round: number;
  surplus: number;
}

export class GameScene extends Phaser.Scene {
  private ship!: Ship;
  private asteroids: Asteroid[] = [];
  private bullets: Bullet[] = [];
  private resources: Resource[] = [];
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private fireKey!: Phaser.Input.Keyboard.Key;
  private tractorKey!: Phaser.Input.Keyboard.Key;
  private tractorGfx!: Phaser.GameObjects.Graphics;

  private surplus = 100;
  private round = 0;
  bootPaused = true;
  private roundTimeLeftMs = 0;
  private beaconGfx!: Phaser.GameObjects.Graphics;
  private readonly beaconRadius = 40;

  private fireRateLevel = 0;
  private driveLevel = 0;
  private pickupRadiusBonus = BASE_PICKUP_RADIUS_BONUS;
  private hasSecondBarrel = false;
  private hasSharperEye = false;
  private hasRicochet = false;
  private hasSensorCourtesy = false;
  private forgivingHullLevel = 0;
  private forgivingHullCharges = 0;
  private bulletColor = 0x44ff77;
  private sensorGfx!: Phaser.GameObjects.Graphics;

  private surplusText!: Phaser.GameObjects.Text;
  private roundText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private centerText!: Phaser.GameObjects.Text;

  private hullIntegrity = INITIAL_HULL_INTEGRITY;
  private cascadeFired = false;

  private lastFireAt = 0;
  private respawnAt = 0;

  constructor() {
    super("GameScene");
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0x000000);

    // Beacon renders beneath the ship.
    this.beaconGfx = this.add.graphics();
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const r = this.beaconRadius;
    const dashCount = 12;
    this.beaconGfx.lineStyle(2, 0x22cc55, 0.85);
    for (let i = 0; i < dashCount; i++) {
      const startAngle = (i / dashCount) * Math.PI * 2;
      const endAngle = ((i + 0.55) / dashCount) * Math.PI * 2;
      this.beaconGfx.beginPath();
      this.beaconGfx.arc(cx, cy, r, startAngle, endAngle, false);
      this.beaconGfx.strokePath();
    }

    this.add.text(cx, cy + r + 6, 'DROP-OFF', {
      fontFamily: 'ui-monospace, monospace',
      fontSize: '10px',
      color: '#22cc55',
    }).setOrigin(0.5, 0).setAlpha(0.75);

    this.ship = new Ship(this, GAME_WIDTH / 2, GAME_HEIGHT / 2);
    this.ship.invulnUntil = this.time.now + SPAWN_INVULN_MS;

    const kb = this.input.keyboard;
    if (!kb) throw new Error("keyboard unavailable");
    this.cursors = kb.createCursorKeys();
    this.fireKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.tractorKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.Z);
    this.tractorGfx = this.add.graphics();
    this.sensorGfx = this.add.graphics();

    const hudStyle = {
      fontFamily: "ui-monospace, monospace",
      fontSize: "18px",
      color: "#22cc55",
    } as const;

    this.surplusText = this.add.text(18, 14, `QUOTA ${this.surplus}`, {
      ...hudStyle,
      color: "#44ff77",
    });
    this.roundText = this.add
      .text(GAME_WIDTH - 18, 14, "", hudStyle)
      .setOrigin(1, 0);
    this.timerText = this.add
      .text(GAME_WIDTH / 2, 14, "", hudStyle)
      .setOrigin(0.5, 0);
    this.centerText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 80, "", {
        ...hudStyle,
        fontSize: "30px",
        align: "center",
      })
      .setOrigin(0.5);

    // Scanlines — static, drawn once, sits above all gameplay objects.
    const scanGfx = this.add.graphics();
    for (let y = 2; y < GAME_HEIGHT; y += 4) {
      scanGfx.lineStyle(1, 0x000000, 0.10);
      scanGfx.lineBetween(0, y, GAME_WIDTH, y);
    }
    scanGfx.setDepth(100);

    // Edge vignette — 10px fade.
    const vigGfx = this.add.graphics();
    const edgePx = 10;
    for (let i = 0; i < edgePx; i++) {
      const alpha = ((edgePx - i) / edgePx) * 0.85;
      vigGfx.fillStyle(0x000000, alpha);
      vigGfx.fillRect(i, 0, 1, GAME_HEIGHT);                    // left
      vigGfx.fillRect(GAME_WIDTH - 1 - i, 0, 1, GAME_HEIGHT);   // right
      vigGfx.fillRect(0, i, GAME_WIDTH, 1);                     // top
      vigGfx.fillRect(0, GAME_HEIGHT - 1 - i, GAME_WIDTH, 1);   // bottom
    }
    vigGfx.setDepth(101);

  }

  override update(_time: number, deltaMs: number): void {
    if (this.bootPaused) return;

    const dt = deltaMs / 1000;
    const now = this.time.now;

    this.updateShip(dt, now);
    this.updateBullets(dt, now);
    this.updateAsteroids(dt);
    this.updateTractor(dt);
    this.updateResources(dt, now);
    this.handleBulletHits(now);
    this.handleShipCollision(now);
    this.handlePickups();
    this.checkDeposit();
  }

  startScene1Flight(): void {
    this.bootPaused = false;
    this.hullIntegrity = INITIAL_HULL_INTEGRITY;
    this.surplus = 0;
    this.surplusText.setText(`QUOTA ${this.surplus}`);
    // One large slow asteroid, off-center.
    const angle = Math.random() * Math.PI * 2;
    const speed = 25;
    this.asteroids.push(
      new Asteroid(
        this,
        GAME_WIDTH * 0.25,
        GAME_HEIGHT * 0.3,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed,
        "large",
      ),
    );
  }

  pauseScene1(): void {
    this.bootPaused = true;
  }

  resumeScene1(): void {
    this.bootPaused = false;
  }

  triggerBolideCascade(): void {
    if (this.cascadeFired) return;
    this.cascadeFired = true;
    this.bootPaused = true;
    if (this.input.keyboard) this.input.keyboard.enabled = false;

    const overlay = this.add.graphics().setDepth(200);
    const STATIC_MS = 600;
    const staticEnd = this.time.now + STATIC_MS;

    const tick = this.time.addEvent({
      delay: 60,
      loop: true,
      callback: () => {
        if (this.time.now >= staticEnd) {
          tick.remove();
          overlay.clear();
          overlay.fillStyle(0x000000, 1);
          overlay.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
          this.events.emit("bolide-cascade", {});
          return;
        }
        overlay.clear();
        for (let i = 0; i < 600; i++) {
          const x = Math.random() * GAME_WIDTH;
          const y = Math.random() * GAME_HEIGHT;
          const g = Math.floor(Math.random() * 200);
          overlay.fillStyle((g << 16) | (g << 8) | g, 1);
          overlay.fillRect(x, y, 2, 2);
        }
      },
    });
  }

  private checkDeposit(): void {
    if (!this.ship.alive) return;
    if (this.surplus <= 0) return;
    if (this.resources.length > 0) return;
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    if (distance(this.ship.x, this.ship.y, cx, cy) < this.beaconRadius) {
      const amount = this.surplus;
      this.surplus = 0;
      this.surplusText.setText(`QUOTA ${this.surplus}`);
      this.events.emit("deposit", { amount });
    }
  }

  private updateTractor(dt: number): void {
    this.tractorGfx.clear();
    const active = this.ship.alive && this.tractorKey.isDown;
    if (active !== this.ship.tractorOn) {
      this.ship.tractorOn = active;
    }
    if (!active) return;

    const sx = this.ship.x;
    const sy = this.ship.y;
    this.tractorGfx.fillStyle(0x22cc55, 0.08);
    this.tractorGfx.fillCircle(sx, sy, TRACTOR_RANGE);
    this.tractorGfx.lineStyle(1, 0x22cc55, 0.35);
    this.tractorGfx.strokeCircle(sx, sy, TRACTOR_RANGE);

    for (const r of this.resources) {
      const dx = sx - r.x;
      const dy = sy - r.y;
      const dist = Math.hypot(dx, dy);
      if (dist > TRACTOR_RANGE || dist < 1) continue;
      const nx = dx / dist;
      const ny = dy / dist;
      r.vx += nx * TRACTOR_PULL_ACCEL * dt;
      r.vy += ny * TRACTOR_PULL_ACCEL * dt;
      const speed = Math.hypot(r.vx, r.vy);
      if (speed > TRACTOR_MAX_PULL_SPEED) {
        r.vx = (r.vx / speed) * TRACTOR_MAX_PULL_SPEED;
        r.vy = (r.vy / speed) * TRACTOR_MAX_PULL_SPEED;
      }
      this.tractorGfx.lineStyle(1.5, 0x44ff77, 0.5);
      this.tractorGfx.beginPath();
      this.tractorGfx.moveTo(sx, sy);
      this.tractorGfx.lineTo(r.x, r.y);
      this.tractorGfx.strokePath();
    }
  }

  private updateShip(dt: number, now: number): void {
    if (!this.ship.alive) {
      if (now >= this.respawnAt) this.tryRespawn(now);
      this.ship.syncView(now);
      return;
    }

    const rotate = BASE_ROTATE_SPEED * Math.pow(DRIVE_MULT_PER_LEVEL, this.driveLevel);
    const thrustAccel = BASE_THRUST_ACCEL * Math.pow(DRIVE_MULT_PER_LEVEL, this.driveLevel);
    if (this.cursors.left.isDown) this.ship.rotation -= rotate * dt;
    if (this.cursors.right.isDown) this.ship.rotation += rotate * dt;

    const thrust = this.cursors.up.isDown;
    const reverse = !thrust && this.cursors.down.isDown;
    const accel = thrust ? thrustAccel : reverse ? -REVERSE_ACCEL : 0;
    if (accel !== 0) {
      this.ship.vx += Math.cos(this.ship.rotation) * accel * dt;
      this.ship.vy += Math.sin(this.ship.rotation) * accel * dt;
      const speed = Math.hypot(this.ship.vx, this.ship.vy);
      if (speed > MAX_SPEED) {
        this.ship.vx = (this.ship.vx / speed) * MAX_SPEED;
        this.ship.vy = (this.ship.vy / speed) * MAX_SPEED;
      }
    }
    if (thrust !== this.ship.thrusting || reverse !== this.ship.reversing) {
      this.ship.thrusting = thrust;
      this.ship.reversing = reverse;
      this.ship.redraw();
    }

    this.ship.x = wrap(this.ship.x + this.ship.vx * dt, GAME_WIDTH);
    this.ship.y = wrap(this.ship.y + this.ship.vy * dt, GAME_HEIGHT);
    this.ship.syncView(now);

    if (Phaser.Input.Keyboard.JustDown(this.fireKey)) this.tryFire(now);
  }

  private currentFireCooldownMs(): number {
    return BASE_FIRE_COOLDOWN_MS * Math.pow(FIRE_COOLDOWN_MULT, this.fireRateLevel);
  }

  private tryFire(now: number): void {
    if (now - this.lastFireAt < this.currentFireCooldownMs()) return;
    const barrels = this.hasSecondBarrel ? 2 : 1;
    if (this.bullets.length > MAX_BULLETS * 2 - barrels) return;
    this.lastFireAt = now;
    const cos = Math.cos(this.ship.rotation);
    const sin = Math.sin(this.ship.rotation);
    const perpX = -sin;
    const perpY = cos;
    const offsets: number[] = this.hasSecondBarrel ? [-6, 6] : [0];
    for (const off of offsets) {
      const tipX = this.ship.x + cos * 16 + perpX * off;
      const tipY = this.ship.y + sin * 16 + perpY * off;
      const vx = cos * BULLET_SPEED + this.ship.vx;
      const vy = sin * BULLET_SPEED + this.ship.vy;
      const b = new Bullet(
        this,
        tipX,
        tipY,
        vx,
        vy,
        now + BULLET_LIFETIME_MS,
        this.bulletColor,
      );
      if (this.hasRicochet) b.hitsRemaining = 2;
      if (this.hasSharperEye) b.homing = true;
      this.bullets.push(b);
    }
  }

  private updateBullets(dt: number, now: number): void {
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      if (now >= b.expiresAt) {
        b.destroy();
        this.bullets.splice(i, 1);
        continue;
      }
      if (b.homing) this.applyHoming(b, dt);
      b.x = wrap(b.x + b.vx * dt, GAME_WIDTH);
      b.y = wrap(b.y + b.vy * dt, GAME_HEIGHT);
      b.syncView();
    }
  }

  private applyHoming(b: Bullet, dt: number): void {
    const speed = Math.hypot(b.vx, b.vy);
    if (speed < 0.01) return;
    const dirX = b.vx / speed;
    const dirY = b.vy / speed;
    let best: { dx: number; dy: number; d: number } | null = null;
    for (const a of this.asteroids) {
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const d = Math.hypot(dx, dy);
      if (d > HOMING_RANGE || d < 1) continue;
      const nx = dx / d;
      const ny = dy / d;
      if (nx * dirX + ny * dirY < HOMING_CONE_COS) continue;
      if (!best || d < best.d) best = { dx, dy, d };
    }
    if (!best) return;
    const targetDir = Math.atan2(best.dy, best.dx);
    const currentDir = Math.atan2(b.vy, b.vx);
    let delta = targetDir - currentDir;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    const step = Math.sign(delta) * Math.min(Math.abs(delta), HOMING_TURN_RATE * dt);
    const nd = currentDir + step;
    b.vx = Math.cos(nd) * speed;
    b.vy = Math.sin(nd) * speed;
  }

  private updateAsteroids(dt: number): void {
    for (const a of this.asteroids) {
      a.x = wrap(a.x + a.vx * dt, GAME_WIDTH);
      a.y = wrap(a.y + a.vy * dt, GAME_HEIGHT);
      a.rotation += a.rotSpeed * dt;
      a.syncView();
    }
    this.updateSensorHighlights();
  }

  private updateSensorHighlights(): void {
    this.sensorGfx.clear();
    if (!this.hasSensorCourtesy || !this.ship.alive) return;
    const cos = Math.cos(this.ship.rotation);
    const sin = Math.sin(this.ship.rotation);
    for (const a of this.asteroids) {
      const dx = a.x - this.ship.x;
      const dy = a.y - this.ship.y;
      const d = Math.hypot(dx, dy);
      if (d > SENSOR_RANGE || d < 1) continue;
      if ((dx / d) * cos + (dy / d) * sin < SENSOR_CONE_COS) continue;
      this.sensorGfx.lineStyle(2, 0x22cc55, 0.55);
      this.sensorGfx.strokeCircle(a.x, a.y, a.radius + 4);
    }
  }

  private handleBulletHits(now: number): void {
    for (let i = this.asteroids.length - 1; i >= 0; i--) {
      const a = this.asteroids[i];
      let hitIdx = -1;
      for (let j = 0; j < this.bullets.length; j++) {
        const b = this.bullets[j];
        if (distance(a.x, a.y, b.x, b.y) < a.radius + b.radius) {
          hitIdx = j;
          break;
        }
      }
      if (hitIdx === -1) continue;

      const bullet = this.bullets[hitIdx];
      bullet.hitsRemaining -= 1;
      if (bullet.hitsRemaining <= 0) {
        bullet.destroy();
        this.bullets.splice(hitIdx, 1);
      }

      this.spawnResource(a, now);

      const next = splitSize(a.size);
      if (next) this.spawnShards(a.x, a.y, next);

      a.destroy();
      this.asteroids.splice(i, 1);
      this.events.emit("rock-destroyed", { size: a.size });
    }
  }

  private spawnResource(a: Asteroid, now: number): void {
    this.resources.push(
      new Resource(
        this,
        a.x,
        a.y,
        a.vx * RESOURCE_DRIFT_FACTOR,
        a.vy * RESOURCE_DRIFT_FACTOR,
        now + RESOURCE_LIFETIME_MS,
      ),
    );
  }

  private updateResources(dt: number, now: number): void {
    for (let i = this.resources.length - 1; i >= 0; i--) {
      const r = this.resources[i];
      if (now >= r.expiresAt) {
        r.destroy();
        this.resources.splice(i, 1);
        continue;
      }
      r.x = wrap(r.x + r.vx * dt, GAME_WIDTH);
      r.y = wrap(r.y + r.vy * dt, GAME_HEIGHT);
      r.rotation += r.rotSpeed * dt;
      r.syncView(now);
    }
  }

  private handlePickups(): void {
    if (!this.ship.alive) return;
    for (let i = this.resources.length - 1; i >= 0; i--) {
      const r = this.resources[i];
      const radius = this.ship.radius + r.collectRadius + this.pickupRadiusBonus;
      if (distance(this.ship.x, this.ship.y, r.x, r.y) < radius) {
        r.destroy();
        this.resources.splice(i, 1);
        this.surplus += SURPLUS_PER_PICKUP;
        this.surplusText.setText(`QUOTA ${this.surplus}`);
        this.events.emit("resource-collected", { amount: SURPLUS_PER_PICKUP });
      }
    }
  }

  private spawnShards(x: number, y: number, size: AsteroidSize): void {
    for (let k = 0; k < 2; k++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Phaser.Math.FloatBetween(35, 85);
      this.asteroids.push(
        new Asteroid(this, x, y, Math.cos(angle) * speed, Math.sin(angle) * speed, size),
      );
    }
  }

  private handleShipCollision(now: number): void {
    if (!this.ship.alive) return;
    if (now < this.ship.invulnUntil) return;
    for (let i = this.asteroids.length - 1; i >= 0; i--) {
      const a = this.asteroids[i];
      if (distance(this.ship.x, this.ship.y, a.x, a.y) < this.ship.radius + a.radius) {
        this.spawnResource(a, now);
        const next = splitSize(a.size);
        if (next) this.spawnShards(a.x, a.y, next);
        a.destroy();
        this.asteroids.splice(i, 1);
        this.events.emit("rock-destroyed", { size: a.size });
        this.hullIntegrity = Math.max(0, this.hullIntegrity - 1);
        this.bootPaused = true;
        this.events.emit("hull-strike", { integrity: this.hullIntegrity });
        return;
      }
    }
  }

  private tryRespawn(now: number): void {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const safe = !this.asteroids.some(
      (a) => distance(a.x, a.y, cx, cy) < a.radius + 120,
    );
    if (!safe) return;
    this.ship.x = cx;
    this.ship.y = cy;
    this.ship.vx = 0;
    this.ship.vy = 0;
    this.ship.rotation = -Math.PI / 2;
    this.ship.alive = true;
    this.ship.invulnUntil = now + SPAWN_INVULN_MS;
    this.ship.redraw();
  }

  startRound(): void {
    this.round += 1;
    this.forgivingHullCharges = this.forgivingHullLevel;
    this.roundTimeLeftMs = ROUND_DURATION_MS;
    this.centerText.setText("");
    this.roundText.setText(`WAVE ${this.round}`);
    this.timerText.setText(formatTimer(this.roundTimeLeftMs));
    for (const a of this.asteroids) a.destroy();
    for (const b of this.bullets) b.destroy();
    for (const r of this.resources) r.destroy();
    this.asteroids = [];
    this.bullets = [];
    this.resources = [];
    const count = BASE_ASTEROIDS + this.round;
    for (let i = 0; i < count; i++) {
      this.asteroids.push(this.spawnEdgeAsteroid());
    }
    this.ship.invulnUntil = this.time.now + SPAWN_INVULN_MS;
  }

  getSurplus(): number {
    return this.surplus;
  }

  spendSurplus(amount: number): boolean {
    if (this.surplus < amount) return false;
    this.surplus -= amount;
    this.surplusText.setText(`QUOTA ${this.surplus}`);
    return true;
  }

  upgradeFireRate(): void {
    this.fireRateLevel += 1;
  }

  upgradeDrive(): void {
    this.driveLevel += 1;
  }

  upgradePickupRadius(): void {
    this.pickupRadiusBonus += PICKUP_RADIUS_PER_LEVEL;
  }

  enableSecondBarrel(): void {
    this.hasSecondBarrel = true;
  }

  enableSharperEye(): void {
    this.hasSharperEye = true;
  }

  enableRicochet(): void {
    this.hasRicochet = true;
  }

  enableSensorCourtesy(): void {
    this.hasSensorCourtesy = true;
  }

  upgradeForgivingHull(): void {
    this.forgivingHullLevel += 1;
    this.forgivingHullCharges += 1;
  }

  setBulletColor(color: number): void {
    this.bulletColor = color;
  }

  setFlareColor(color: number): void {
    this.ship.flareColor = color;
    this.ship.redraw();
  }

  setLiveryColor(color: number): void {
    this.ship.strokeColor = color;
    this.ship.redraw();
  }

  getFireRateLevel(): number {
    return this.fireRateLevel;
  }

  getPickupBonusPx(): number {
    return this.pickupRadiusBonus;
  }

  private spawnEdgeAsteroid(): Asteroid {
    let x: number, y: number;
    if (Math.random() < 0.5) {
      x = Math.random() < 0.5 ? 0 : GAME_WIDTH;
      y = Math.random() * GAME_HEIGHT;
    } else {
      x = Math.random() * GAME_WIDTH;
      y = Math.random() < 0.5 ? 0 : GAME_HEIGHT;
    }
    const angle = Math.random() * Math.PI * 2;
    const speed = Phaser.Math.FloatBetween(25, 65);
    return new Asteroid(
      this,
      x,
      y,
      Math.cos(angle) * speed,
      Math.sin(angle) * speed,
      "large",
    );
  }

}

function formatTimer(ms: number): string {
  const secs = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
